import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { sanitizeHtml } from '../../utils/Tools';

interface IFairOption {
  Id: string;
  Name: string;
  Description?: string;
  Grades?: string;
  LogoUrl?: string;
}

interface ISelectFairData {
  FairLabel?: string;
  AutoSelect?: boolean;
  RedirectTo?: string;
  Fairs?: IFairOption[];
}

/**
 * Replaces SelectFair.ascx on Default.aspx — multi-fair picker.
 */
const SelectFairPage = () => {
  const [data, setData] = useState<ISelectFairData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    store.server
      .postApi<ServerResponse<ISelectFairData>>('../SelectFair/Data', {})
      .then((x) => {
        if (x.Success && x.Value) {
          if (x.Value.AutoSelect && x.Value.RedirectTo) {
            window.location.href = x.Value.RedirectTo;
            return;
          }
          setData(x.Value);
        } else {
          toast.error(x.Message || 'Unable to load fairs.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to load fairs.', { autoClose: false }))
      .finally(() => setLoading(false));
  }, []);

  const selectFair = (id: string) => {
    if (selecting) return;
    setSelecting(true);
    store.server
      .postApi<ServerResponse<{ RedirectTo?: string }>>('../SelectFair/Select', { fairId: id })
      .then((x) => {
        if (x.Success && x.Value?.RedirectTo) {
          window.location.href = x.Value.RedirectTo;
          return;
        }
        toast.error(x.Message || 'Unable to select fair.', { autoClose: false });
        setSelecting(false);
      })
      .catch(() => {
        toast.error('Unable to select fair.', { autoClose: false });
        setSelecting(false);
      });
  };

  if (loading || !data) {
    return (
      <div className="main-body-page grow" data-testid="select-fair-page" style={{ minHeight: 120 }}>
        <p>Loading…</p>
      </div>
    );
  }

  const label = data.FairLabel || 'fair';

  return (
    <div className="main-body-page grow" data-testid="select-fair-page" style={{ minHeight: 120 }}>
      <h2>Click the {label} you wish to enter!</h2>
      <div data-testid="select-fair-list">
        {(data.Fairs || []).length === 0 && (
          <div className="alert alert-info" data-testid="select-fair-empty">
            No public {label}s are available.
          </div>
        )}
        {(data.Fairs || []).map((f) => (
            <div
              key={f.Id}
              className="panel panel-default panel-hover"
              data-testid={`select-fair-item-${f.Id}`}
              style={{ cursor: selecting ? 'wait' : 'pointer', marginBottom: 12 }}
              onClick={() => selectFair(f.Id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') selectFair(f.Id);
              }}
            >
              <div className="panel-heading">
                <strong>{f.Name}</strong>
              </div>
              <div className="panel-body">
                <div className="row">
                  <div className="col-sm-3 text-center">
                    {f.LogoUrl ? (
                      <img
                        src={f.LogoUrl}
                        alt={f.Name}
                        className="logo-max-size"
                        style={{ maxHeight: 80, maxWidth: '100%' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : null}
                  </div>
                  <div className="col-sm-9">
                    {f.Grades ? (
                      <p>
                        <span className="label label-info">{f.Grades}</span>
                      </p>
                    ) : null}
                    {f.Description ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(f.Description) }} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SelectFairPage;
